(() => {
  if (window.yolaCorporateInitialized) return;
  window.yolaCorporateInitialized = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let heroObserver;
  let headerResize;
  let embedObserver;
  let openDialog;
  let returnFocus;
  let unlockDialog;
  let finishDialog;
  let unlockMenu;
  let menuTimer;
  const syncVideoControl = video => {
    const control = video.closest('[data-yc-hero]')?.querySelector('[data-yc-video-toggle]');
    if (!control) return;
    control.setAttribute('aria-label', video.paused ? 'Play background video' : 'Pause background video');
    const play = control.querySelector('[data-yc-play-icon]');
    const pause = control.querySelector('[data-yc-pause-icon]');
    if (play) play.hidden = !video.paused;
    if (pause) pause.hidden = video.paused;
  };
  const loadResponsiveVideo = video => {
    const mobile = window.matchMedia('(max-width: 600px)').matches;
    video.poster = mobile ? video.dataset.mobilePoster : video.dataset.desktopPoster;
    if (!video.getAttribute('src')) {
      video.src = mobile ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      video.load();
    }
  };
  // Native modal dialogs contain focus. A second trap focused the container on touch.
  const closeDialog = () => { openDialog?.close(); finishDialog?.(); };
  document.addEventListener('pointerdown', () => { document.documentElement.dataset.ycInput = 'pointer'; }, true);
  document.addEventListener('keydown', event => {
    if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) document.documentElement.dataset.ycInput = 'keyboard';
  }, true);
  const lockPage = () => {
    const root = document.documentElement;
    const body = document.body;
    const x = window.scrollX, y = window.scrollY;
    const styles = ['position', 'top', 'left', 'right', 'overflow'].map(key => [key, body.style[key]]);
    const overflow = root.style.overflow;
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
    return () => {
      styles.forEach(([key, value]) => { body.style[key] = value; });
      root.style.overflow = overflow;
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo({left:x, top:y, behavior:'instant'});
      root.style.scrollBehavior = behavior;
    };
  };
  const closeMenu = (immediate = false) => {
    const header = document.querySelector('[data-yc-header]');
    const toggle = header?.querySelector('[data-yc-menu-toggle]');
    const menu = document.getElementById('YolaMobileMenu');
    if (!toggle || !menu?.open) return;
    clearTimeout(menuTimer);
    const finish = () => {
      menu.close();
      menu.classList.remove('is-closing');
      toggle.setAttribute('aria-expanded', 'false');
      header.classList.remove('is-menu-open');
      unlockMenu?.();
      unlockMenu = null;
      if (toggle.offsetParent !== null) toggle.focus({preventScroll:true});
    };
    if (immediate || reduced.matches) finish();
    else { menu.classList.add('is-closing'); menuTimer = setTimeout(finish, 220); }
  };
  const revealInquiry = () => {
    if (location.hash !== '#YolaCorporateContact') return;
    const tabs = document.querySelector('[data-yc-contact-tabs]');
    if (tabs?.dataset.bound) selectContactTab(tabs, 'YCFormTab', false);
  };
  const selectContactTab = (root, id, focus) => {
    root.querySelectorAll('[role="tab"]').forEach(tab => {
      const selected = tab.id === id;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      document.getElementById(tab.getAttribute('aria-controls')).hidden = !selected;
      if (selected && focus) tab.focus({preventScroll:true});
    });
  };
  const init = () => {
    document.querySelectorAll('[data-yc-contact-tabs]').forEach(root => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      root.querySelector('[role="tablist"]').hidden = false;
      selectContactTab(root, root.querySelector('[data-form-feedback]') ? 'YCFormTab' : 'YCEmailTab', false);
      root.addEventListener('click', event => { const tab = event.target.closest('[role="tab"]'); if (tab) selectContactTab(root, tab.id, false); });
      root.addEventListener('keydown', event => {
        const tabs = [...root.querySelectorAll('[role="tab"]')];
        const index = tabs.indexOf(event.target);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        selectContactTab(root, tabs[next].id, true);
      });
      root.querySelector('form').addEventListener('invalid', () => selectContactTab(root, 'YCFormTab', false), true);
    });
    revealInquiry();
    heroObserver?.disconnect();
    headerResize?.disconnect();
    embedObserver?.disconnect();
    const embed = document.querySelector('.yc-instagram-native');
    if (embed) {
      const labelEmbed = () => { const frame = embed.querySelector('iframe'); if (frame && !frame.title) frame.title = 'Yola Beauty on Instagram'; };
      labelEmbed();
      embedObserver = new MutationObserver(labelEmbed);
      embedObserver.observe(embed, {childList:true});
    }
    const header = document.querySelector('[data-yc-header]');
    const hero = document.querySelector('[data-yc-hero]');
    if (!header) return;
    const drawer = document.querySelector('[data-yc-drawer]');
    if (drawer && !drawer.dataset.bound) {
      drawer.dataset.bound = 'true';
      drawer.addEventListener('cancel', event => { event.preventDefault(); closeMenu(); });
    }
    header.classList.toggle('yc-header--light', !!hero?.classList.contains('yc-hero--video'));
    const observeHero = () => {
      heroObserver?.disconnect();
      if (!hero) { header.classList.add('is-solid'); return; }
      heroObserver = new IntersectionObserver(([entry]) => header.classList.toggle('is-solid', !entry.isIntersecting), {rootMargin: `-${header.querySelector('.yc-header__inner').offsetHeight}px 0px 0px 0px`});
      heroObserver.observe(hero);
    };
    observeHero();
    headerResize = new ResizeObserver(observeHero);
    headerResize.observe(header.querySelector('.yc-header__inner'));
    const video = hero?.querySelector('video');
    if (video?.hasAttribute('data-yc-responsive-video') && !video.dataset.bound) {
      video.dataset.bound = 'true';
      video.muted = true;
      video.addEventListener('play', () => syncVideoControl(video));
      video.addEventListener('pause', () => syncVideoControl(video));
      if (!reduced.matches) {
        loadResponsiveVideo(video);
        video.play().catch(() => syncVideoControl(video));
      }
    }
    if (video && reduced.matches) { video.autoplay = false; video.pause(); }
    const control = hero?.querySelector('[data-yc-video-toggle]');
    if (control && video) syncVideoControl(video);
  };
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-yc-menu-toggle]');
    if (toggle) {
      const menu = document.getElementById(toggle.getAttribute('aria-controls'));
      if (menu.open) { closeMenu(); return; }
      clearTimeout(menuTimer);
      menu.classList.remove('is-closing');
      unlockMenu = lockPage();
      toggle.setAttribute('aria-expanded', 'true');
      toggle.closest('[data-yc-header]').classList.add('is-menu-open');
      menu.showModal();
      menu.querySelector('[data-yc-menu-close]').focus({preventScroll:true});
      return;
    }
    const drawer = document.querySelector('[data-yc-drawer]');
    if (event.target.closest('[data-yc-menu-close]')) { closeMenu(); return; }
    if (event.target.closest('.yc-drawer a')) closeMenu(true);
    if (event.target === drawer) {
      const rect = drawer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right) closeMenu();
    }
    const marqueeToggle = event.target.closest('[data-yc-marquee-toggle]');
    if (marqueeToggle) {
      const paused = marqueeToggle.getAttribute('aria-pressed') !== 'true';
      marqueeToggle.setAttribute('aria-pressed', String(paused));
      marqueeToggle.setAttribute('aria-label', paused ? 'Resume brand animation' : 'Pause brand animation');
      marqueeToggle.closest('[data-yc-marquee]').classList.toggle('is-paused', paused);
    }
    const trigger = event.target.closest('[data-yc-dialog]');
    if (trigger) {
      const dialog = document.getElementById(trigger.dataset.ycDialog);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      if (openDialog) return;
      returnFocus = trigger;
      unlockDialog = lockPage();
      openDialog = dialog;
      dialog.showModal();
      dialog.querySelector('[data-yc-close]').focus({preventScroll:true});
      const onCancel = event => { event.preventDefault(); closeDialog(); };
      const cleanup = () => {
        dialog.removeEventListener('close', cleanup);
        dialog.removeEventListener('cancel', onCancel);
        unlockDialog?.();
        unlockDialog = null;
        openDialog = null;
        finishDialog = null;
        if (returnFocus?.isConnected) returnFocus.focus({preventScroll:true});
      };
      finishDialog = cleanup;
      dialog.addEventListener('close', cleanup, {once:true});
      dialog.addEventListener('cancel', onCancel);
      return;
    }
    if (event.target.closest('[data-yc-close]')) closeDialog();
    if (event.target === openDialog) {
      const rect = openDialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog();
    }
    const videoToggle = event.target.closest('[data-yc-video-toggle]');
    if (videoToggle) {
      const video = videoToggle.closest('[data-yc-hero]').querySelector('video');
      if (video.hasAttribute('data-yc-responsive-video')) loadResponsiveVideo(video);
      if (video.paused) video.play().then(() => videoToggle.setAttribute('aria-label','Pause background video')).catch(() => {});
      else { video.pause(); videoToggle.setAttribute('aria-label','Play background video'); }
    }
  });
  window.matchMedia('(min-width: 901px)').addEventListener('change', event => { if (event.matches) closeMenu(true); });
  reduced.addEventListener('change', () => { if (reduced.matches) document.querySelector('[data-yc-hero] video')?.pause(); });
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('hashchange', revealInquiry);
  document.addEventListener('shopify:section:unload', () => { closeMenu(true); closeDialog(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
})();
